import { Activity, DimensionVector, Recommendation, ResidentNeed, ScoringOptions, WellnessDimension } from '../types/recommendations';

const DIMENSIONS: WellnessDimension[] = ['mind', 'body', 'social', 'spirit', 'environment', 'purpose', 'financial', 'other'];

/** The dimension an activity most strongly supports (argmax of its mix), or null if unmixed. */
function primaryDimension(mix: DimensionVector): WellnessDimension | null {
  let best: WellnessDimension | null = null;
  let bestVal = 0;
  for (const d of DIMENSIONS) {
    const v = mix[d] ?? 0;
    if (v > bestVal) { bestVal = v; best = d; }
  }
  return best;
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function dot(a: Record<string, number>, b: Record<string, number>): number {
  let sum = 0;
  for (const d of Object.keys(a)) {
    const av = a[d] ?? 0;
    const bv = b[d] ?? 0;
    sum += av * bv;
  }
  return sum;
}

export function scoreActivities(
  activities: Activity[],
  resident: ResidentNeed,
  options?: ScoringOptions
): Recommendation[] {
  const needVector: Record<string, number> = {};
  for (const d of DIMENSIONS) needVector[d] = clamp01(resident.need[d] ?? 0);

  const pref: Record<string, number> = {};
  for (const d of DIMENSIONS) pref[d] = clamp01(options?.preferenceBoost?.[d] ?? 0);

  const diversityPenalty = clamp01(options?.diversityPenaltyFactor ?? 0);
  const noveltyBoost = clamp01(options?.noveltyBoost ?? 0);

  const results: Recommendation[] = activities.map((a) => {
    const mix: Record<string, number> = {};
    for (const d of DIMENSIONS) mix[d] = clamp01(a.mix[d] ?? 0);

    // Base: alignment of activity mix with resident need
    const base = dot(needVector, mix);

    // Preference: small boost where resident expressed likes
    const prefScore = 0.2 * dot(pref, mix);

    // Simple novelty: if no tags or not attended recently, small fixed boost (to be wired later)
    const novelty = 0.1 * noveltyBoost;

    // Diversity penalty placeholder (to be applied against last recommended dimension externally)
    const diversity = -0.1 * diversityPenalty; // constant until we track history

    const score = base + prefScore + novelty + diversity;

    // Reasons: top 2 contributing dimensions by need * mix
    const contributions: Array<{ dimension: WellnessDimension; contribution: number }> = DIMENSIONS
      .map(d => ({ dimension: d, contribution: (needVector[d] ?? 0) * (mix[d] ?? 0) }))
      .filter(x => x.contribution > 0)
      .sort((a, b) => b.contribution - a.contribution)
      .slice(0, 2);

    return {
      ...a,
      score,
      reasons: contributions
    };
  });

  results.sort((a, b) => b.score - a.score);
  return results;
}

/**
 * Pick up to `max` activities that spread across the resident's need dimensions,
 * instead of letting the single highest-need dimension fill every slot.
 *
 * The scorer alone ranks purely by need × mix, so a resident whose strongest
 * dimension has enough events sees only that dimension — a resident with goals in
 * body, mind and spirit would get five body recommendations and nothing else.
 *
 * `scored` must already be ordered best-first. Activities are grouped by their
 * dominant dimension. Slots fill in two tiers ("force diversity if you have goals
 * across multiple dimensions" — the July 2026 commitment, no further):
 *   1. Round-robin across dimensions the resident actually has need in (need > 0),
 *      in descending order of need — so a resident with body+mind+spirit goals
 *      gets all three represented before any repeats, while a resident with ONE
 *      goal gets recommendations concentrated on that goal's dimension.
 *   2. Only when the need dimensions run out of events do zero-need dimensions
 *      backfill (round-robin), followed by dimensionless activities last.
 * With an all-zero need vector every dimension is tier 2, which preserves the
 * plain spread behavior.
 */
export function selectDiverse<T extends Activity>(
  scored: T[],
  need: DimensionVector,
  max: number
): T[] {
  if (max <= 0 || scored.length === 0) return [];

  const buckets = new Map<WellnessDimension, T[]>();
  const noDimension: T[] = [];
  for (const item of scored) {
    const dim = primaryDimension(item.mix);
    if (!dim) { noDimension.push(item); continue; }
    const bucket = buckets.get(dim);
    if (bucket) bucket.push(item);
    else buckets.set(dim, [item]);
  }

  // Highest-need dimensions first. Ties / zero-need dimensions keep insertion order.
  const orderedDims = Array.from(buckets.keys()).sort(
    (a, b) => (need[b] ?? 0) - (need[a] ?? 0)
  );
  const needDims = orderedDims.filter((d) => (need[d] ?? 0) > 0);
  const zeroDims = orderedDims.filter((d) => (need[d] ?? 0) <= 0);

  const picked: T[] = [];
  const cursor = new Map<WellnessDimension, number>(orderedDims.map((d) => [d, 0]));

  const roundRobin = (dims: WellnessDimension[]) => {
    let advanced = true;
    while (picked.length < max && advanced) {
      advanced = false;
      for (const dim of dims) {
        if (picked.length >= max) break;
        const i = cursor.get(dim)!;
        const bucket = buckets.get(dim)!;
        if (i < bucket.length) {
          picked.push(bucket[i]);
          cursor.set(dim, i + 1);
          advanced = true;
        }
      }
    }
  };

  roundRobin(needDims);
  roundRobin(zeroDims);

  // Backfill remaining slots with activities that have no dominant dimension.
  for (const item of noDimension) {
    if (picked.length >= max) break;
    picked.push(item);
  }

  return picked;
}




